import './App.css'
import './BookerForm.scss'
import BookerWrapper, { BookerWrapperForceOptions, BookerWrapperTheme } from './components/BookerWrapper'
import { useAppDispatch, useAppSelector } from './store/hooks'
import { useEffect } from 'react'
import { getSiteConfigThunk } from './features/siteConfigSlice'
import { getColors, getCustomization } from 'utils/layout'
import { Booker, BookerVersions, NavBar, Portal } from 'booker'
import { isVertical } from './utils/siteConfigUtils'
import { IntlProvider } from 'react-intl'
import { Bot } from 'maia'
import resources from './resources'
import {
   addUserChatMessage,
   chatHistoryThunk,
   chatThunk,
   rateConversationThunk,
   setMessageRating,
} from './features/chatSlice'
import { getHotelListThunk } from './features/hotelListSlice'
import { getFontsThunk } from './features/fontsSlice'
import webfontloader from 'webfontloader'

type Props = {
   accountCode: string
   locale: 'es-mx' | 'en-us'
   hideNavbar?: boolean
   dataOnlyFormToShow: 'HOTEL_FLIGHT' | 'HOTEL' | 'SERVICES' | null
   hotelId?: number
   customUri?: string
   forceDestinationIdOnUrl?: boolean
   searchParams: URLSearchParams
   version?: BookerVersions
   theme?: BookerWrapperTheme
   devOptions?: BookerWrapperForceOptions
   isEmbedded?: boolean
}

function App({
   accountCode,
   locale,
   hideNavbar,
   dataOnlyFormToShow,
   hotelId,
   customUri,
   forceDestinationIdOnUrl,
   searchParams,
   version,
   theme,
   devOptions,
   isEmbedded,
}: Props) {
   const dispatch = useAppDispatch()
   const siteConfig = useAppSelector((state) => state.siteConfig)
   const chat = useAppSelector((state) => state.chat)
   const hotelListWrapper = useAppSelector((state) => state.hotelListWrapper)
   const fontsWrapper = useAppSelector((state) => state.fontsWrapper)

   useEffect(() => {
      dispatch(getSiteConfigThunk({ account: accountCode, locale, customUri }))
      dispatch(getFontsThunk({ account: accountCode, locale }))
   }, [dispatch, accountCode, customUri, locale])

   useEffect(() => {
      if (!fontsWrapper.isFetching && !fontsWrapper.isPristine && fontsWrapper.data) {
         webfontloader.load({
            google: {
               families: fontsWrapper.data.families || [],
            },
         })
      }
   }, [fontsWrapper])

   useEffect(() => {
      if (siteConfig.data?.account.code) {
         dispatch(getHotelListThunk({ account: siteConfig.data.account.code, locale }))
      }
   }, [dispatch, siteConfig.data?.account.code, locale])

   useEffect(() => {
      if (chat.data?.threadId) {
         sessionStorage.setItem('chatThreadId', chat.data.threadId)
      }
   }, [chat.data?.threadId])

   useEffect(() => {
      if (siteConfig.data?.account.code && siteConfig.data?.chatbotConfig?.enabled) {
         const threadId = sessionStorage.getItem('chatThreadId')
         if (threadId) {
            dispatch(
               chatHistoryThunk({
                  locale: locale || 'es-mx',
                  account: siteConfig.data.account.code,
                  threadId: threadId,
               }),
            )
         } else {
            dispatch(
               chatThunk({
                  account: siteConfig.data.account.code,
                  locale: locale || 'es-mx',
               }),
            )
         }
      }
   }, [locale, siteConfig.data?.account.code, siteConfig.data?.chatbotConfig?.enabled, dispatch])

   function onSubmitBot(message: string, code?: string) {
      dispatch(addUserChatMessage({ content: message }))
      dispatch(
         chatThunk({
            ...(chat.data?.threadId && { threadId: chat.data.threadId }),
            account: siteConfig.data?.account.code || '',
            locale: locale || 'es-mx',
            message,
            ...(code ? { skill: code } : {}),
         }),
      )
   }

   function setDestinationToSession(destinationId: number, message: string) {
      sessionStorage.setItem('selectedDestinationId', destinationId.toString())
      dispatch(addUserChatMessage({ content: message }))
      dispatch(
         chatThunk({
            ...(chat.data?.threadId && { threadId: chat.data.threadId }),
            account: siteConfig.data?.account.code || '',
            locale: locale || 'es-mx',
            message,
            destinationId,
         }),
      )
   }

   function setHotelToSession(hotelId: number, message: string) {
      sessionStorage.setItem('selectedHotelId', hotelId.toString())
      dispatch(addUserChatMessage({ content: message }))
      dispatch(
         chatThunk({
            ...(chat.data?.threadId && { threadId: chat.data.threadId }),
            account: siteConfig.data?.account.code || '',
            locale: locale || 'es-mx',
            message,
            hotelId,
         }),
      )
   }

   const devTheme = Object.assign({}, siteConfig.data?.theme, theme) as BookerWrapperTheme | null
   const { baseFontFamilies, ...restDevTheme } = devTheme || {} // se quitan las baseFontFamilies para que nos e carguen en el root (solo se usan en el booking engine)
   const colors = devTheme ? getColors(devTheme) : null
   const customization = devTheme ? getCustomization(restDevTheme) : null

   const allResources = {
      'es-mx': {
         ...resources['es-mx'],
         ...(siteConfig.data?.i18n && siteConfig.data.i18n['es-mx']),
         ...(siteConfig.data?.chatbotConfig?.i18n &&
            Object.fromEntries(
               Object.entries(siteConfig.data.chatbotConfig.i18n['es'] || {}).map(([k, v]) => [`maia_chatbot_${k}`, v]),
            )),
      },
      'en-us': {
         ...resources['en-us'],
         ...(siteConfig.data?.i18n && siteConfig.data.i18n['en-us']),
         ...(siteConfig.data?.chatbotConfig?.i18n &&
            Object.fromEntries(
               Object.entries(siteConfig.data.chatbotConfig.i18n['en'] || {}).map(([k, v]) => [`maia_chatbot_${k}`, v]),
            )),
      },
   }

   return (
      <>
         <style>{`
            :root {
            
            ${customization?.join(' ')}
            ${colors?.join(' ')}

            }
         `}</style>
         <IntlProvider locale={locale} messages={allResources[locale]}>
            <div>
               {siteConfig.isPristine || siteConfig.isFetching ? (
                  <Booker
                     vertical={isVertical()}
                     locale={''}
                     dateFormat={''}
                     useTailwindPrefix
                     version={version}
                     {...(devTheme && { styleOptions: devTheme })}
                     showArrows={devOptions?.showArrows || false}
                     showLabels={devOptions?.showLabels || false}
                     inline={devOptions?.inline || false}
                  >
                     {!hideNavbar && (!dataOnlyFormToShow || dataOnlyFormToShow === 'HOTEL') && (
                        <NavBar selected="hotel" useTailwindPrefix>
                           <NavBar.HotelItem isClear />
                        </NavBar>
                     )}
                     <Booker.Loading />
                  </Booker>
               ) : siteConfig.data ? (
                  <BookerWrapper
                     siteConfig={siteConfig.data}
                     dataOnlyFormToShow={dataOnlyFormToShow}
                     hotelId={hotelId}
                     forceDestinationIdOnUrl={forceDestinationIdOnUrl}
                     version={version}
                     searchParams={searchParams}
                     isEmbedded={isEmbedded}
                     {...(devTheme && { theme: devTheme })}
                     {...(devOptions && { forceOptions: devOptions })}
                  />
               ) : null}
            </div>
            {!isEmbedded &&
               siteConfig.data &&
               siteConfig.data.account &&
               ((siteConfig.data.chatbotConfig?.enabled && siteConfig.data.chatbotConfig?.show) ||
                  devOptions?.forceChatbot) && (
                  <Portal className={'itm-promotion-portal'}>
                     <Bot
                        history={chat.data?.items}
                        onSubmit={onSubmitBot}
                        isFetching={chat.isFetchingNewMessage}
                        useTailwindPrefix
                        {...(siteConfig.data.chatbotConfig && {
                           theme: { primaryColor: siteConfig.data.chatbotConfig.color },
                           options: {
                              name: siteConfig.data.chatbotConfig.name,
                              accountName: siteConfig.data.account.name,
                              avatar: siteConfig.data.chatbotConfig.avatar,
                           },
                           notification: siteConfig.data.chatbotConfig.notification,
                           position: siteConfig.data.chatbotConfig.position === 'LEFT' ? 'bottom-left' : 'bottom-right',
                           offset: siteConfig.data.chatbotConfig.offset,
                        })}
                        siteConfig={siteConfig.data}
                        onSkillSelect={({ prompt, code }) => onSubmitBot(prompt, code)}
                        onHotelSelect={({ prompt, hotelId }) => {
                           setHotelToSession(hotelId, prompt)
                        }}
                        onDestinationSelect={({ prompt, destinationId }) => {
                           setDestinationToSession(destinationId, prompt)
                        }}
                        isRatingConversation={chat.isRating}
                        ratingStatus={chat.ratingStatus}
                        ratings={chat.ratings}
                        onRateConversation={(isPositive, messageKey) => {
                           if (messageKey) dispatch(setMessageRating({ messageKey, isPositive: isPositive ?? false }))
                           if (chat.data?.threadId)
                              dispatch(
                                 rateConversationThunk({
                                    threadId: chat.data.threadId,
                                    isPositive: isPositive ?? false,
                                    account: siteConfig.data?.account.code,
                                    locale: locale || 'es-mx',
                                    ...(messageKey && { messageId: messageKey }),
                                 }),
                              )
                        }}
                        onDrawHotelRate={({ roomId, quoteRequest }) => {
                           const selectedHotelId = sessionStorage.getItem('selectedHotelId')
                           const resultHotelId =
                              quoteRequest?.hotelId ||
                              hotelId ||
                              (selectedHotelId ? parseInt(selectedHotelId) : undefined)

                           const room = hotelListWrapper.items
                              .find((h) => h.id === resultHotelId)
                              ?.rooms?.find((r) => r.id === roomId)
                           return {
                              ...(room &&
                                 room.photos.length > 0 && {
                                    photo: {
                                       base: room.photos[0].basePath,
                                       path: room.photos[0].path,
                                    },
                                 }),
                           }
                        }}
                     />
                  </Portal>
               )}
         </IntlProvider>
      </>
   )
}

export default App
